import React, { Suspense, lazy, useEffect, useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, CircularProgress, Card, Skeleton } from '@mui/material';
import { FuelMonitoringThemeProvider } from './theme/ThemeProvider';
import { Layout } from './components/layout';
import { realTimeDataManager, SiteData, AlertData } from './utils/mockData';
import LoginPage from './components/auth/LoginPage';
import { tokenStorage } from './utils/auth';
import SignupPage from './components/auth/SignupPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import Presentation from './components/presentation/Presentation';

// Lazy load components to reduce initial bundle size
const OverviewTab = lazy(() => import('./components/dashboard/tabs/OverviewTab'));
const AnalyticsTab = lazy(() => import('./components/dashboard/tabs/AnalyticsTab'));
const QualityTab = lazy(() => import('./components/dashboard/tabs/QualityTab'));
const SecurityTab = lazy(() => import('./components/dashboard/tabs/SecurityTab'));
const MapTab = lazy(() => import('./components/dashboard/tabs/MapTab'));
const DevicesTab = lazy(() => import('./components/dashboard/tabs/DevicesTab'));
const SettingsTab = lazy(() => import('./components/dashboard/tabs/SettingsTab'));
const TankersTab = lazy(() => import('./components/dashboard/tabs/TankersTab'));
const ReportTab = lazy(() => import('./components/dashboard/tabs/ReportTab'));

interface UserData {
  id: string;
  name: string;
  email: string;
  role: 'Admin' | 'Manager' | 'Operator' | 'Auditor';
  avatar?: string;
  permissions: string[];
  sites: string[];
}

const TAB_TO_PATH: Record<number, string> = {
  0: '/dashboard',
  1: '/analytics',
  2: '/monitoring/fuel',
  3: '/security',
  4: '/sites/map',
  5: '/devices',
  6: '/settings',
  7: '/sites/tankers',
  8: '/reports',
};

const PATH_TO_TAB: Record<string, number> = Object.fromEntries(
  Object.entries(TAB_TO_PATH).map(([k, v]) => [v, Number(k)])
);

function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sites, setSites] = useState<SiteData[]>([]);
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const activeTab = PATH_TO_TAB[location.pathname] ?? 0;
  const [user, setUser] = useState<UserData | null>(() => {
    try {
      const saved = localStorage.getItem('fuelMonitoringAuth');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const handleLogin = (userData: UserData) => {
    setUser(userData);
    localStorage.setItem('fuelMonitoringAuth', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('fuelMonitoringAuth');
    tokenStorage.clear();
  };

  useEffect(() => {
    const unsubscribe = realTimeDataManager.subscribe((siteData, alertData) => {
      setSites(siteData);
      setAlerts(alertData);
    });

    realTimeDataManager.startRealTimeUpdates(15000); // Reduced frequency to 15 seconds

    return () => {
      unsubscribe();
      realTimeDataManager.stopRealTimeUpdates();
    };
  }, []);

  // Memoize notifications to prevent unnecessary re-renders
  const notifications = useMemo(() => ({
    count: alerts.filter(alert => !alert.acknowledged).length,
    items: alerts.slice(0, 3).map(alert => ({ // Reduced to 3 items
      id: alert.id,
      title: alert.title,
      message: alert.message,
      time: new Date(alert.timestamp).toLocaleString(),
      severity: alert.severity === 'critical' ? 'error' as const :
                alert.severity === 'high' || alert.severity === 'medium' ? 'warning' as const :
                'info' as const,
    })),
  }), [alerts]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(TAB_TO_PATH[newValue] ?? '/dashboard');
  };

  const handleSidebarNavigation = (path: string) => {
    if (PATH_TO_TAB[path] !== undefined) navigate(path);
  };

  const LoadingFallback = () => (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <CircularProgress
            size={24}
            sx={{ color: 'rgba(43, 103, 176, 1)' }}
          />
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            Loading dashboard...
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {[...Array(6)].map((_, index) => (
            <Card key={index}>
              <Box sx={{ p: 2 }}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 1 }} />
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="80%" height={20} />
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );

  if (!user) {
    return (
      <FuelMonitoringThemeProvider>
        <LoginPage onLogin={handleLogin} />
      </FuelMonitoringThemeProvider>
    );
  }

  return (
    <FuelMonitoringThemeProvider>
      <Layout
        currentPath={location.pathname}
        user={user}
        notifications={notifications}
        onNavigate={handleSidebarNavigation}
        onLogout={handleLogout}
      >
        <Box>
            <Card
              sx={{
                mb: 3,
                bgcolor: '#ffffff',
                border: '1px solid rgba(99,155,230,0.15)',
                borderRadius: '14px',
                overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(15,23,42,0.08)',
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTab-root': {
                    fontWeight: 500,
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                    textTransform: 'none',
                    minHeight: { xs: '44px', sm: '48px' },
                    minWidth: { xs: 90, sm: 110 },
                    px: { xs: 1.5, sm: 2 },
                    mx: 0.25,
                    borderRadius: '8px',
                    color: '#6B7280',
                    transition: 'all 0.15s ease',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(15,23,42,0.07)',
                      color: '#0F172A',
                      fontWeight: 700,
                    },
                    '&:hover': {
                      bgcolor: 'rgba(15,23,42,0.04)',
                      color: '#374151',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    bgcolor: '#0F172A',
                    height: 2.5,
                    borderRadius: '2px 2px 0 0',
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: '#0F172A',
                    '&.Mui-disabled': { opacity: 0.3 },
                  },
                  px: 1.5,
                  py: 0.75,
                }}
              >
                <Tab label="Overview" />
                <Tab label="Analytics" />
                <Tab label="Quality" />
                <Tab label="Security" />
                <Tab label="Map" />
                <Tab label="Devices" />
                <Tab label="Settings" />
                <Tab label="Tankers" />
                <Tab label="Reports" />
              </Tabs>
            </Card>

          <Suspense fallback={<LoadingFallback />}>
            {activeTab === 0 && <OverviewTab sites={sites} alerts={alerts} userRole={user.role} />}
            {activeTab === 1 && <AnalyticsTab sites={sites} userRole={user.role} />}
            {activeTab === 2 && <QualityTab sites={sites} alerts={alerts} />}
            {activeTab === 3 && <SecurityTab sites={sites} alerts={alerts} />}
            {activeTab === 4 && <MapTab sites={sites} alerts={alerts} />}
            {activeTab === 5 && <DevicesTab sites={sites} />}
            {activeTab === 6 && <SettingsTab sites={sites} user={user} />}
            {activeTab === 7 && <TankersTab sites={sites} />}
            {activeTab === 8 && <ReportTab sites={sites} alerts={alerts} userRole={user.role} />}
          </Suspense>
        </Box>
      </Layout>
    </FuelMonitoringThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/presentation" element={<Presentation />} />
        <Route path="/signup" element={<FuelMonitoringThemeProvider><SignupPage /></FuelMonitoringThemeProvider>} />
        <Route path="/forgot-password" element={<FuelMonitoringThemeProvider><ForgotPasswordPage /></FuelMonitoringThemeProvider>} />
        <Route path="/reset-password" element={<FuelMonitoringThemeProvider><ResetPasswordPage /></FuelMonitoringThemeProvider>} />
        <Route path="/*" element={<Dashboard />} />
      </Routes>
    </Router>
  );
}

export default App;
